import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import Modal from '@/components/ui/Modal'
import Button from '@/components/ui/Button'
import Label from '@/components/ui/Label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'
import { SlotPickerSection } from '@/components/ui/SlotPickerSection'
import { ModalFooter } from '@/components/ui/ConfirmModal'
import { ZipInput } from '@/components/ui/ZipInput'
import { ChevronDown, Check } from 'lucide-react'
import { VisitType } from '@/api/enums'
import { formatCents } from '@/utils/cancellation-policy'
import { TELEHEALTH_BADGE, IN_PERSON_BADGE } from '@/features/search/constants'
import { useSpecialties } from '@/api/hooks/specialties.hooks'
import { useSpecialistsDropdown } from '@/api/hooks/specialists.hooks'
import { useAvailableTimeSlots } from '@/api/hooks/appointment-slots.hooks'
import { useProposeNewDoctor } from '@/api/hooks/appointments.hooks'
import { toastCallbacks } from '@/api/hooks/useMutationWithToast'
import { formatDateKey } from '@/utils/date'
import { groupSlotsByDateKey, findNextAvailableDate } from '@/utils/appointments'
import type { AppointmentResponse } from '@/api/contracts/appointments.contract'
import type { SpecialistDropdownItem, SpecialistDropdownLocation } from '@/api/contracts/specialists.contract'
import type { Specialty } from '@/api/contracts/specialties.contract'
import type { AvailableTimeSlotsDto } from '@/api/contracts/appointment-slots.contract'

export function NewDoctorModal({ appointment, onClose }: { appointment: AppointmentResponse; onClose: () => void }) {
  const navigate = useNavigate()
  const proposeDoctor = useProposeNewDoctor()

  const [specialty, setSpecialty] = useState<Specialty | null>(null)
  const [specialist, setSpecialist] = useState<SpecialistDropdownItem | null>(null)
  const [location, setLocation] = useState<SpecialistDropdownLocation | null>(null)
  const [zipCode, setZipCode] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDate, setSelectedDate] = useState<Date | undefined>()
  const [selectedSlot, setSelectedSlot] = useState<AvailableTimeSlotsDto | undefined>()
  const [isSpecialtyOpen, setIsSpecialtyOpen] = useState(false)
  const [isDoctorOpen, setIsDoctorOpen] = useState(false)

  const { data: specialties = [] } = useSpecialties()
  const { data: specialists = [], isLoading: isLoadingDoctors } = useSpecialistsDropdown(
    specialty && zipCode.length >= 5 ? { specialty: specialty.slug, zipCode } : null
  )
  const { data: allSlots = [], isLoading: isSlotsLoading } = useAvailableTimeSlots({
    slug: specialist?.specialistSlug ?? '',
    ...(location?.id && { locationId: location.id }),
  })

  const grouped = useMemo(() => groupSlotsByDateKey(allSlots), [allSlots])
  const availableDates = useMemo(() => Object.keys(grouped), [grouped])
  const slotsForDate = useMemo(() => (selectedDate ? grouped[formatDateKey(selectedDate)] ?? [] : []), [grouped, selectedDate])
  const filteredDoctors = useMemo(
    () => specialists.filter((s) => !searchQuery || `${s.specialistFirstName ?? ''} ${s.specialistLastName ?? ''}`.toLowerCase().includes(searchQuery.toLowerCase())),
    [specialists, searchQuery]
  )

  const resetSlots = () => { setSelectedDate(undefined); setSelectedSlot(undefined) }

  const handleConfirm = () => {
    if (!specialist || !selectedSlot) return
    proposeDoctor.mutate(
      {
        currentAppointmentId: appointment.id,
        specialistId: specialist.specialistId,
        ...(location?.id && { locationId: location.id }),
        userEmail: appointment.userEmail,
        firstName: appointment.firstName ?? '',
        lastName: appointment.lastName ?? '',
        userPhoneNumber: appointment.userPhoneNumber || '',
        appointmentDate: selectedSlot.dateTime,
        visitType: appointment.visitType,
        symptoms: appointment.symptoms,
        dateOfBirth: appointment.dateOfBirth ?? '',
        emailMarketingConsent: appointment.emailMarketingConsent ?? false,
        smsConsent: appointment.smsConsent ?? false,
        ...(appointment.searchedZipCode
          ? { searchedZipCode: appointment.searchedZipCode }
          : {}),
      },
      toastCallbacks({
        onSuccess: 'New appointment created',
        onSuccessFn: () => { onClose(); navigate({ to: '/admin' }) },
      })
    )
  }

  const doctorName = (s: SpecialistDropdownItem) =>
    `${s.specialistFirstName ?? ''} ${s.specialistLastName ?? ''}`.trim() || 'Specialist'

  const visitBadge = (s: SpecialistDropdownItem) => {
    const isTele = s.specialistVisitType === VisitType.Telemedicine || s.specialistVisitType === VisitType.Both
    const isInPerson = s.specialistVisitType === VisitType.InPerson || s.specialistVisitType === VisitType.Both
    return (
      <span className="flex gap-1">
        {isInPerson && <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${IN_PERSON_BADGE.className}`}>{IN_PERSON_BADGE.label}</span>}
        {isTele && <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${TELEHEALTH_BADGE.className}`}>{TELEHEALTH_BADGE.label}</span>}
      </span>
    )
  }

  // Whatever actually settled on the current appointment, whichever side of the
  // notice window it sits on: an admin move refunds in full either way.
  const settledFeeCents =
    appointment.cancellationPolicy.refundAmountCents +
    appointment.cancellationPolicy.retainedAmountCents

  return (
    <Modal isOpen onClose={onClose} title='Propose New Appointment'>
      <div className='space-y-6'>
        <p className='text-sm text-muted-foreground'>This will cancel the current request and create a new appointment.</p>

        {/* The $50 is a destination charge sitting in the current doctor's
            connected account, so it cannot follow the patient. It has to come
            back out and be taken again for the new provider, which the patient
            sees on their statement as charge, refund, charge. Say so here so no
            admin sets that in motion without knowing. */}
        {settledFeeCents > 0 && (
          <p className='rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800'>
            Dr. {appointment.specialistName} is holding the {formatCents(settledFeeCents)} appointment fee for this booking.
            Moving the patient refunds that {formatCents(settledFeeCents)} and charges it again when the new appointment is
            confirmed. The patient is emailed about the move and told about both.
          </p>
        )}

        {/* Specialty */}
        <div className='space-y-2'>
          <Label className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>Specialty</Label>
          <Popover open={isSpecialtyOpen} onOpenChange={setIsSpecialtyOpen}>
            <PopoverTrigger asChild>
              <Button variant='outline' className='w-full justify-between font-medium h-11'>
                <span className={specialty ? 'text-sm font-medium text-foreground' : 'text-muted-foreground'}>
                  {specialty?.name || 'Select a specialty...'}
                </span>
                <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
              </Button>
            </PopoverTrigger>
            <PopoverContent className='min-w-[200px] p-0' align='start' side='bottom'>
              <div className='max-h-[300px] overflow-y-auto p-1'>
                {specialties.map((s) => (
                  <div
                    key={s.slug}
                    className={`relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm hover:bg-accent hover:text-accent-foreground ${specialty?.slug === s.slug ? 'bg-accent text-accent-foreground' : ''}`}
                    onClick={() => { setSpecialty(s); setIsSpecialtyOpen(false); setSpecialist(null); setLocation(null); setSearchQuery(''); resetSlots() }}
                  >
                    <span className='absolute left-2 flex h-3.5 w-3.5 items-center justify-center'>{specialty?.slug === s.slug && <Check className='h-4 w-4' />}</span>
                    <span className='font-medium'>{s.name || 'Unnamed'}</span>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Zip Code */}
        {specialty && (
          <div className='space-y-2'>
            <Label className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>Zip Code</Label>
            <ZipInput value={zipCode} setValue={(val) => { setZipCode(val); setSpecialist(null); setLocation(null); resetSlots() }} />
          </div>
        )}

        {/* Doctor */}
        {specialty && zipCode.length >= 5 && (
          <div className='space-y-2'>
            <Label className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>Doctor</Label>
            {isLoadingDoctors ? (
              <div className='flex h-11 items-center justify-center rounded-md border border-input bg-background text-sm text-muted-foreground'>Loading doctors...</div>
            ) : (
              <Popover open={isDoctorOpen} onOpenChange={setIsDoctorOpen}>
                <PopoverTrigger asChild>
                  <Button variant='outline' className='w-full justify-between font-medium h-11'>
                    <span className={specialist ? 'text-sm font-medium text-foreground' : 'text-muted-foreground'}>
                      {specialist ? doctorName(specialist) : 'Select a doctor...'}
                    </span>
                    <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='min-w-[200px] p-0' align='start' side='bottom'>
                  <div className='p-2 border-b'>
                    <input type='text' placeholder='Search doctors...' value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2' onClick={(e) => e.stopPropagation()} />
                  </div>
                  <div className='max-h-[300px] overflow-y-auto'>
                    {filteredDoctors.length === 0 ? (
                      <div className='p-4 text-center text-sm text-muted-foreground'>No doctor found.</div>
                    ) : (
                      <div className='p-1'>
                        {filteredDoctors.map((s, index) => (
                          <div
                            key={index}
                            className={`relative flex w-full cursor-pointer select-none items-start rounded-sm py-2 pl-8 pr-2 text-sm hover:bg-accent hover:text-accent-foreground ${specialist === s ? 'bg-accent text-accent-foreground' : ''}`}
                            onClick={() => { setSpecialist(s); setIsDoctorOpen(false); setLocation(s.location); resetSlots() }}
                          >
                            <span className='absolute left-2 flex h-3.5 w-3.5 items-center justify-center'>{specialist === s && <Check className='h-4 w-4' />}</span>
                            <div>
                              <div className='flex items-center gap-2'>
                                <span className='font-medium'>{doctorName(s)}{s.specialistSuffix ? `, ${s.specialistSuffix}` : ''}</span>
                                {visitBadge(s)}
                              </div>
                              {s.location && (
                                <div className='text-xs text-muted-foreground'>
                                  {[s.location.practiceName, s.location.city, s.location.state].filter(Boolean).join(', ')}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}

        {/* Slot picker */}
        {specialist && (
          <SlotPickerSection
            selectedDate={selectedDate}
            onDateSelect={(d) => { setSelectedDate(d); setSelectedSlot(undefined) }}
            availableDates={availableDates}
            slotsForDate={slotsForDate}
            inPersonSlots={slotsForDate.filter((s) => s.locationId !== null)}
            telemedicineSlots={slotsForDate.filter((s) => s.locationId === null)}
            isLoading={isSlotsLoading}
            selectedSlot={selectedSlot}
            onSlotSelect={setSelectedSlot}
            nextAvailableDate={findNextAvailableDate(availableDates)}
          />
        )}

        <ModalFooter
          secondaryLabel='Cancel'
          onClose={onClose}
          primaryLabel='Create New Appointment & Notify Patient'
          onPrimary={handleConfirm}
          primaryDisabled={!specialist || !selectedSlot}
        />
      </div>
    </Modal>
  )
}
